import FormInputComponent from "@via/components/inputs/form-input-component";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { useSaveFilesToCollection } from "@via/pages/data/data-collections/data-collections.api.tsx";
import GeoEditableTable from "@via/pages/data/data-sources/api-data-sources/geo/geo-editable-table.tsx";
import GeoFileTable from "@via/pages/data/data-sources/api-data-sources/geo/geo-file-table.tsx";
import { useGeoSearch } from "@via/pages/data/data-sources/api-data-sources/geo/geo.api.tsx";
import FileOrganizerForm, {
  type IFileOrganizerForm,
} from "@via/pages/data/file-organizer/file-organizer-form.tsx";
import { requiredValidation } from "@via/utils/form-validators";
import { removeFalsyValues } from "@via/utils/helpers.tsx";
import { DataSourceProvider, FileArrayItem, GeoItem, GeoSearch } from "common";
import { type JSX, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { prepareGEOSearch } from "@via/pages/data/data-sources/api-data-sources/utils/helpers";
import DataSourceTitle from "@via/pages/data/data-sources/data-source-title.tsx";
import GeoLogo from "/geo-logo.png";
import ProgressBar from "@via/components/progress-bar/progress-bar";
import { useParams } from "react-router-dom";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import PremiumSampleOrganizer from "@via/pages/data/file-organizer/premium-sample-organizer.tsx";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";

export const exampleGeoIds = ["GSM1331276", "GSE55190", "SRR10095965"];

export default function GeoSource(): JSX.Element {
  const { isSelectedCollectionPremium } = useGlobal();
  const { geoId } = useParams();
  const { setNotification, NOTIFICATION } = useNotifications();
  const [searchList, setSearchList] = useState<Set<string>>(new Set());
  const [failedSearches, setFailedSearches] = useState<Map<string, string>>(
    new Map()
  );
  const [geoListMap, setGeoListMap] = useState<Map<string, GeoItem>>(new Map());
  const [selectedGeoList, setSelectedGeoList] = useState<GeoItem[]>([]);
  const [percentage, setPercentage] = useState<number>(0);

  const percentageCallback = percent => {
    setPercentage(percent);
  };

  const { mutateAsync: getGeoSearch, isPending: isGetGeoSearchLoading } =
    useGeoSearch();

  const geoList = useMemo(() => Array.from(geoListMap.values()), [geoListMap]);
  const failedIds = useMemo(
    () => Array.from(failedSearches.keys()),
    [failedSearches]
  );

  const { control, handleSubmit, setValue } = useForm<GeoSearch>({
    mode: "onChange",
    defaultValues: {
      geoSearchText: geoId ? geoId : "",
    },
  });

  useEffect(() => {
    if (geoId) {
      handleSubmit(handleGeoApiSearchFormSubmit)();
    }
  }, [geoId]);

  const {
    mutateAsync: saveFilesToCollection,
    isPending: isSaveFilesToCollectionLoading,
  } = useSaveFilesToCollection();

  const runGeoSearch = async (
    search: string,
    ctx: { searched: Set<string>; failed: Map<string, string> }
  ) => {
    if (ctx.searched.has(search) && !ctx.failed.has(search)) {
      setNotification({
        id: "geo-search-already-searched",
        message: "Already searched",
        details: `You have already searched for ${search}`,
        variant: NOTIFICATION.ALERT,
      });
      return;
    }

    try {
      const result = await getGeoSearch({ search, percentageCallback });
      if (result?.data?.length) {
        ctx.searched.add(search);
        ctx.failed.delete(search);
        setGeoListMap(prevList => {
          const updatedList = new Map(prevList);
          result?.data?.forEach(item => {
            if (!updatedList.has(item.srrId)) {
              updatedList.set(item.srrId, item);
            }
          });
          return updatedList;
        });
      } else {
        ctx.failed.set(search, "No results found");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      ctx.failed.set(search, message);
    }
  };

  const handleGeoApiSearchFormSubmit = async (payload: GeoSearch) => {
    const geoSearchList = prepareGEOSearch(payload.geoSearchText);
    const searched = new Set(searchList);
    const failed = new Map(failedSearches);
    for (const search of geoSearchList) {
      await runGeoSearch(search, { searched, failed });
    }
    setSearchList(searched);
    setFailedSearches(failed);
    setPercentage(0);
  };

  const handleRetryFailed = async () => {
    if (!failedIds.length) return;
    const searched = new Set(searchList);
    const failed = new Map(failedSearches);
    for (const id of failedIds) {
      await runGeoSearch(id, { searched, failed });
    }
    setSearchList(searched);
    setFailedSearches(failed);
    setPercentage(0);
  };

  const handleAddToSelected = (selectedItems: GeoItem[]) => {
    const updatedGeoList = (geoList || []).filter(
      item => !selectedItems.some(selected => selected.srrId === item.srrId)
    );

    setGeoListMap(new Map(updatedGeoList.map(item => [item.srrId, item])));

    setSelectedGeoList(prevSelected => [
      ...selectedItems,
      ...(prevSelected || []),
    ]);
  };

  const handleUpdateSelectedGeoItemName = (srrId: string, newName: string) => {
    setSelectedGeoList(prevSelected => {
      return (prevSelected || []).map(item => {
        if (item.srrId === srrId) {
          return { ...item, name: newName };
        }
        return item;
      });
    });
  };

  const handleRemoveSelectedGeoItem = (originalItem: GeoItem) => {
    setSelectedGeoList(prevSelected => {
      // Remove the item from selectedGeoList
      const updatedSelectedList = (prevSelected || []).filter(
        item => item.srrId !== originalItem.srrId
      );
      return updatedSelectedList;
    });

    setGeoListMap(prevList => {
      const updatedList = new Map(prevList);
      updatedList.set(originalItem.srrId, originalItem);
      return updatedList;
    });
  };

  const handleSaveSelectedFilesToCollection = (
    formPayload: IFileOrganizerForm
  ): void => {
    const fileArray: FileArrayItem[] = [];

    selectedGeoList?.forEach(geoItem => {
      fileArray.push({ name: geoItem.name, filesUsed: geoItem.srrId });
    });

    const saveFilesToCollectionPayload = {
      dataSource: {
        provider: DataSourceProvider.GEO,
      },
      collectionType: selectedGeoList![0].collectionType,
      ...formPayload,
      fileArray,
    };

    /*
     * Add amazon s3 archive credential id to archive path
     */
    if (saveFilesToCollectionPayload.s3ArchiveCredentialId) {
      saveFilesToCollectionPayload.s3ArchiveDir = `${saveFilesToCollectionPayload.s3ArchiveDir}\t${saveFilesToCollectionPayload.s3ArchiveCredentialId}`;
      delete saveFilesToCollectionPayload.s3ArchiveCredentialId;
    }

    /*
     * Add google cloud archive credential id to archive path
     */
    if (saveFilesToCollectionPayload.gsArchiveCredentialId) {
      saveFilesToCollectionPayload.gsArchiveDir = `${saveFilesToCollectionPayload.gsArchiveDir}\t${saveFilesToCollectionPayload.gsArchiveCredentialId}`;
      delete saveFilesToCollectionPayload.gsArchiveCredentialId;
    }

    saveFilesToCollection(removeFalsyValues(saveFilesToCollectionPayload));
  };

  const setExampleGeoId = (geoId: string) => {
    setValue("geoSearchText", geoId);
  };

  return (
    <div className="flex-auto">
      <DataSourceTitle logoPath={GeoLogo} title="SRA (GEO)" />
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit(handleGeoApiSearchFormSubmit)(e);
        }}>
        <div className="space-y-4 pt-3">
          <FormInputComponent
            name="geoSearchText"
            label="SRA (GEO) accession ID (GSE/GSM/SRR ID):"
            placeholder="GSE/GSM/SRR ID"
            showRequiredField={true}
            control={control}
            disabled={isGetGeoSearchLoading}
            validations={requiredValidation("GSE/GSM/SRR ID")}
          />
          <div className="text-sm font-normal text-gray-500">
            Enter a valid comma or space separated SRA (GEO) accession IDs and
            click “Search”. Foundry will retrieve the corresponding files from
            the SRA GEO database to create a dataset.
            <div className="flex gap-2">
              Example IDs:
              <div>
                {exampleGeoIds.map(geoId => (
                  <button
                    onClick={() => setExampleGeoId(geoId)}
                    disabled={isGetGeoSearchLoading}
                    className="font-bold"
                    key={geoId}>
                    {geoId}&nbsp;
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-shrink-0 justify-end">
            <button
              data-testid="geoSearchButton"
              type="submit"
              disabled={isGetGeoSearchLoading}
              className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              <Spinner loading={isGetGeoSearchLoading}>Search</Spinner>
            </button>
          </div>
          {failedIds.length ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-red-900">
                  <span>Failed IDs ({failedIds.length})</span>
                  <CopyableText
                    value={failedIds.join(",")}
                    variant="danger"
                    showValue={false}
                    buttonLabel="Copy"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleRetryFailed()}
                    disabled={isGetGeoSearchLoading}
                    className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-60">
                    Retry failed
                  </button>
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {failedIds.map(id => (
                  <li
                    key={id}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="font-mono text-red-900">{id}</span>
                      <span className="min-w-0 truncate text-red-700">
                        {failedSearches.get(id)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="text-end">
            {isGetGeoSearchLoading ? (
              <ProgressBar progress={percentage} showPercentage />
            ) : null}
          </div>
        </div>
      </form>
      <div>
        {geoList.length ? (
          <GeoFileTable
            isLoading={isGetGeoSearchLoading}
            geoList={geoList}
            onAddToSelected={handleAddToSelected}
            total={geoList.length}
          />
        ) : null}
      </div>

      {selectedGeoList?.length ? (
        isSelectedCollectionPremium ? (
          <PremiumSampleOrganizer
            collectionType={selectedGeoList[0]?.collectionType || ""}
            provider={DataSourceProvider.GEO}
            selectedGeoList={selectedGeoList}
          />
        ) : (
          <div>
            <GeoEditableTable
              geoList={selectedGeoList}
              total={selectedGeoList.length}
              onUpdateGeoItemName={handleUpdateSelectedGeoItemName}
              onRemoveGeoItem={handleRemoveSelectedGeoItem}
            />

            <FileOrganizerForm
              onFormSave={handleSaveSelectedFilesToCollection}
              isLoading={isSaveFilesToCollectionLoading}
              geoList={selectedGeoList}
            />
          </div>
        )
      ) : null}
    </div>
  );
}
